import Button from '@orn/shared-web/components/Button/Button';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import { ChevronDownIcon, ChevronUpIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { listedAlertToAlertRow } from '../../utils/alert/alertRow';
import { getAlertsCenterStyledPresentation, TRUNCATED_ROW_LIMIT } from '../../utils/alert/alertsCenterCard';
import { SIDE_PANEL_TRANSITION_DURATION_STYLE } from '../../utils/sidePanelMotion';
import AlertListItem from '../AlertListItem';
import AlertSection from '../AlertSection';
import AlertTypeIcon from '../AlertTypeIcon';

import AlertsCenterEmptyState from './AlertsCenterEmptyState';
import AlertsCenterHeader, { ALERTS_CENTER_TITLE } from './AlertsCenterHeader';
import NoActiveAlertsPill from './NoActiveAlertsPill';

import type { ListedAlert } from '../../utils/alert/alertRow';
import type { AlertsCenterStatus, IAlertsCenterSection } from '../../utils/alert/alertsCenter';
import type {
  AlertsCenterLayout,
  AlertsCenterPresentation,
  IAlertsCenterMotion,
} from '../../utils/alert/alertsCenterCard';

export { TRUNCATED_ROW_LIMIT } from '../../utils/alert/alertsCenterCard';
export type { AlertsCenterLayout, AlertsCenterPresentation } from '../../utils/alert/alertsCenterCard';

type IconComponent = React.ComponentType<{ className?: string }>;

/** The glyph points where the card is about to go: the pill expands, the cards collapse. */
const CHEVRON_ICON_BY_PRESENTATION: Record<AlertsCenterPresentation, IconComponent> = {
  collapsed: ChevronUpIcon,
  truncated: ChevronDownIcon,
  full: ChevronDownIcon,
};

const REGION_CLASS = 'flex w-full flex-col';

/** The stacked card's surface. The carousel draws its header and cards on the pane's own ground instead. */
const CARD_CHROME_CLASS = 'bg-app_light border-secondary shadow-card rounded-2xl border';

/**
 * The carousel's track. `py-1 -my-1` gives the cards' shadows room inside the
 * scroller's clip without growing the 84px row the height math counts.
 */
const CAROUSEL_TRACK_CLASS =
  'scrollbar-hide -my-1 flex snap-x snap-mandatory scroll-px-4 gap-2 overflow-x-auto overscroll-x-contain px-4 py-1';

/** Figma's 334px card, narrowed to the track at the 320px floor rather than overflowing it. */
const CAROUSEL_CARD_CLASS = 'w-[334px] max-w-full shrink-0 snap-start';

/** Content that appears or leaves with a presentation fades rather than pops. */
const FADE_CLASS = 'transition-opacity duration-150 ease-in-out motion-reduce:transition-none';

/**
 * The `inert` attribute in the form the React 18 runtime keeps: it drops
 * `inert={true}` as an unknown boolean, while `@types/react` types it as one.
 */
const INERT_ATTRIBUTE = { inert: '' } as unknown as { inert: boolean };

/**
 * How far into a collapse the pill waits before fading in over the shrinking
 * card, so it lands in the motion's last stretch rather than covering rows
 * that are still sliding.
 */
const PILL_FADE_IN_DELAY_CLASS = 'delay-[350ms]';

/**
 * The pill's spoken name — "1 alert to review", "5 alerts to review". The count
 * is drawn in its own badge, so the visible text alone would read "5alerts to
 * review"; this is what the button is labelled with instead.
 */
export function formatAlertsToReview(count: number): string {
  return `${count} ${count === 1 ? 'alert' : 'alerts'} to review`;
}

interface IAlertsPillProps {
  count: number;
  layout: AlertsCenterLayout;
  /** The pill was clicked. Absent on the decorative copy drawn over a moving card. */
  onExpand?: () => void;
  className?: string;
}

const PILL_CLASS = cx(
  'bg-brand-solid mx-auto flex w-fit max-w-full items-center rounded-full',
  'shadow-[0_4px_4px_-1px_rgba(23,20,18,0.03)]'
);

/** The carousel's pill is Figma's 32px "Alerts Pill": a 20px badge in 6px of padding. */
const PILL_SIZE_CLASS: Record<AlertsCenterLayout, string> = {
  list: 'h-[42px] gap-2 pl-2 pr-3',
  carousel: 'h-8 gap-1.5 pl-1.5 pr-2.5',
};

const PILL_BADGE_SIZE_CLASS: Record<AlertsCenterLayout, string> = {
  list: 'size-6',
  carousel: 'size-5',
};

const AlertsPillContent = ({ count, layout }: Pick<IAlertsPillProps, 'count' | 'layout'>) => (
  <>
    {/* `brand-700` off the palette rather than `utility-brand-700`, which
        inverts to a light disc in dark mode and would lose the white numeral. */}
    <span
      className={cx(
        'bg-brand-700 text-primary_on-brand flex shrink-0 items-center justify-center rounded-full text-sm font-bold',
        PILL_BADGE_SIZE_CLASS[layout]
      )}
    >
      {count}
    </span>
    <span className="text-primary_on-brand min-w-0 truncate text-left text-sm font-medium">
      {count === 1 ? 'alert to review' : 'alerts to review'}
    </span>
    <ChevronUpIcon aria-hidden="true" className="text-secondary_on-brand size-4.5 shrink-0" />
  </>
);

/** The collapsed pill: one button at rest, a decorative overlay while the card grows out of it or shrinks into it. */
const AlertsPill = ({ count, layout, onExpand, className }: IAlertsPillProps) =>
  onExpand ? (
    <button
      type="button"
      aria-expanded="false"
      aria-label={formatAlertsToReview(count)}
      onClick={onExpand}
      className={cx(
        PILL_CLASS,
        PILL_SIZE_CLASS[layout],
        'outline-focus-ring cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2',
        className
      )}
    >
      <AlertsPillContent count={count} layout={layout} />
    </button>
  ) : (
    <div aria-hidden="true" className={cx(PILL_CLASS, PILL_SIZE_CLASS[layout], 'pointer-events-none', className)}>
      <AlertsPillContent count={count} layout={layout} />
    </div>
  );

interface IAlertsCenterErrorStateProps {
  onRetry?: () => void;
}

/**
 * Neutral rather than `bg-error-*`: this is a fetch that did not come back, and
 * a red banner would read as something being wrong with the household's money.
 */
const AlertsCenterErrorState = ({ onRetry }: IAlertsCenterErrorStateProps) => (
  <div className="flex flex-col items-center justify-center gap-1 px-6 py-16 text-center">
    <div className="text-primary text-lg font-medium">We couldn’t load your alerts</div>
    <div className="text-tertiary text-sm">Check your connection and try again.</div>
    {onRetry && (
      <Button className="mt-5" size="sm" color="secondary" onClick={onRetry}>
        Try again
      </Button>
    )}
  </div>
);

interface IAlertsCenterBaseProps {
  presentation: AlertsCenterPresentation;
  /**
   * How the pill and the truncated card are drawn — see `AlertsCenterLayout`.
   * On `carousel` the full center also drops its card frame to fill the sheet.
   * @default 'list'
   */
  layout?: AlertsCenterLayout;
  /**
   * The presentation change in flight, or none at rest. While set, the card
   * renders the union of the outgoing and target trees and fades what only one
   * of them shows; the host transitions the card's height around it.
   */
  motion?: IAlertsCenterMotion;
  /**
   * The household's ACTIVE total from the counts read — what the pill and the
   * truncated header show. Not the number of rows fetched.
   */
  activeCount: number;
  /**
   * Whether the counts read has answered. On `carousel` a count of zero draws
   * the no-active-alerts pill only once it has, so a loading or failed read
   * shows nothing rather than a pill the real card would replace. @default true
   */
  isActiveCountKnown?: boolean;
  /** Groups with rows, already ordered and filtered — see `buildAlertsCenterView`. */
  sections: IAlertsCenterSection[];
  /** Whole-surface state; only the full presentation renders its non-ready states. */
  status: AlertsCenterStatus;
  /** The collapsed pill, or the no-active-alerts pill, was clicked. */
  onExpand: () => void;
  /** The truncated card's "View all", or the carousel's expand button, was clicked. */
  onViewAll: () => void;
  /** The header chevron was clicked, in the truncated or the full card. */
  onCollapse: () => void;
  /** The header chevron, for a host that moves focus onto it when the full center opens. */
  collapseButtonRef?: React.Ref<HTMLButtonElement>;
  /** Id of the row drawn with the brand fill. */
  selectedId?: string | null;
  /** Id of the row showing the refusal pill (mid-turn or without a membership), or none — see `AlertListItem`. */
  blockedNoticeAlertId?: string | null;
  /** The refusal pill's text, when the lock is not the mid-turn one — see `AlertListItem`. */
  blockedNoticeText?: string;
  onSelect?: (alert: ListedAlert) => void;
  onDismiss?: (alertId: string) => void;
  /** Retries every failed read. Only reachable while `status` is `error`. */
  onRetry?: () => void;
  /** Rendered below the list — `alertActionSlot` from `useAlertRowAction`. */
  actionSlot?: React.ReactNode;
  /**
   * Reference instant for the rows' relative timestamps. Defaults to now; pass
   * a fixed date to make a story or test deterministic.
   */
  now?: Date;
  className?: string;
}

/**
 * The Notification Center in one of three presentations: a one-line capsule
 * ("N alerts to review"), a card with the first {@link TRUNCATED_ROW_LIMIT}
 * need-attention rows and a "View all" button, or the full center — the pinned
 * header over every group, paged independently. Purely presentational —
 * `useComposerAlertsCenter` owns the reads, the counts, the mutations, and which
 * presentation is showing.
 *
 * The truncated and full cards are one tree: the header, the need-attention
 * `AlertSection`, and its first rows keep their tree positions in both (the
 * unlabeled section renders its header slot as null rather than dropping it),
 * so React keeps those nodes instead of remounting them. Nothing renders when
 * there is nothing to show — the pill at a count of zero, the truncated card
 * with no need-attention rows — so the composer sits flush.
 *
 * The exception is the phone: on `layout="carousel"` the collapsed card at a
 * count of zero draws `NoActiveAlertsPill` in the pill's place once
 * `isActiveCountKnown`, so the composer keeps a way into the full center.
 *
 * On `layout="carousel"` (the phone sheet) the pill is 32px and the truncated
 * card is a header — no count, an expand button beside the chevron — over one
 * horizontal row holding every need-attention row as a card, on the pane's
 * ground. It shares no rows with the full center, so a move between the two
 * swaps the content at its first frame instead of morphing it; a collapse from
 * the full center pins the carousel to the shrinking card's bottom edge.
 *
 * While `motion` is set the card is what moves, whichever presentations are
 * involved: it wears the full center's skeleton (`h-full`, a clipped body that
 * fills it) so the host's height transition has something to stretch, mounts
 * whatever either side shows, and wears the outgoing side's opacities and
 * spacing at `pre` and the target's from `active` — so "View all", the section
 * labels, and the pending / history groups fade and slide instead of popping.
 * The pill is a separate element: it draws over the moving card's bottom and
 * dissolves as the card grows out of it, or lands over the last stretch of a
 * collapse.
 *
 * The header count is the server-side total floored by the rows on screen,
 * exactly as the section headers are: the counts read zero-fills on failure
 * and lags a poll behind a fresh archive.
 */
const AlertsCenterBase = ({
  presentation,
  layout = 'list',
  motion,
  activeCount,
  isActiveCountKnown = true,
  sections,
  status,
  onExpand,
  onViewAll,
  onCollapse,
  collapseButtonRef,
  selectedId,
  blockedNoticeAlertId,
  blockedNoticeText,
  onSelect,
  onDismiss,
  onRetry,
  actionSlot,
  now,
  className,
}: IAlertsCenterBaseProps) => {
  const needAttention = sections.find((section) => section.section === 'needAttention');
  const needAttentionRows = needAttention?.rows ?? [];
  const shownCount = Math.max(activeCount, needAttentionRows.length);
  // Rows the truncated card actually draws, against the household's total. It
  // drops to a bare "(N)" once the cut stops hiding anything.
  const truncatedShownCount = Math.min(needAttentionRows.length, TRUNCATED_ROW_LIMIT);
  const truncatedCount =
    truncatedShownCount < shownCount ? `(${truncatedShownCount} of ${shownCount})` : `(${shownCount})`;

  const showsNoActiveAlertsPill = layout === 'carousel' && isActiveCountKnown && shownCount === 0;

  // At rest the pill needs something to count (or the phone's no-active-alerts
  // pill) and the truncated card needs rows; the full center always renders.
  const rendersNothingAtRest = (target: AlertsCenterPresentation): boolean =>
    target === 'collapsed'
      ? shownCount === 0 && !showsNoActiveAlertsPill
      : target === 'truncated' && needAttentionRows.length === 0;

  // The capsule a collapse lands on and an expand leaves — at rest, or as the
  // decorative copy over a moving card when `onClick` is absent.
  const renderPill = (onClick: (() => void) | undefined, pillClassName: string | undefined) => {
    if (shownCount > 0) {
      return <AlertsPill count={shownCount} layout={layout} onExpand={onClick} className={pillClassName} />;
    }
    return showsNoActiveAlertsPill ? <NoActiveAlertsPill onOpen={onClick} className={pillClassName} /> : null;
  };

  // The card is what animates, so a move to the pill — or to a card that has
  // nothing to show — keeps rendering the tree it is leaving; the target takes
  // over only once the move settles.
  const tree =
    motion && (presentation === 'collapsed' || rendersNothingAtRest(presentation)) ? motion.from : presentation;
  const styledAs = getAlertsCenterStyledPresentation(presentation, motion);

  if (tree === 'collapsed') {
    return renderPill(onExpand, className);
  }

  const isList = layout === 'list';
  const isFullTree = tree === 'full';
  const isCarouselTree = !isList && tree === 'truncated';
  // In the sheet the full center is the sheet's own surface. The carousel it
  // grows out of swaps at a move's first frame, so there is no card to morph from.
  const fillsSheet = !isList && isFullTree;
  const isMoving = motion !== undefined;
  const showsFullContent = isFullTree || motion?.from === 'full';
  // The carousel has no "View all", so the full center growing out of it has none to fade.
  const showsViewAll = isList && (tree === 'truncated' || motion?.from === 'truncated');
  const isFullContentVisible = styledAs === 'full';
  const isViewAllVisible = styledAs === 'truncated';
  const isPillVisible = styledAs === 'collapsed';
  const showsPillOverlay = isMoving && (presentation === 'collapsed' || motion.from === 'collapsed');

  if (!isFullTree && !isMoving && needAttentionRows.length === 0) {
    return null;
  }

  // The truncated card is the full center's need-attention group cut to its
  // first rows, unlabeled and without its pager. A collapse out of the full
  // center keeps every group mounted while they fade.
  const visibleSections: IAlertsCenterSection[] = showsFullContent
    ? sections
    : needAttention
      ? [
          {
            section: 'needAttention',
            count: needAttention.count,
            rows: needAttentionRows.slice(0, TRUNCATED_ROW_LIMIT),
          },
        ]
      : [];
  const showSections = isFullTree ? status === 'ready' : true;
  const ChevronIcon = CHEVRON_ICON_BY_PRESENTATION[tree];

  const renderRow = (alert: ListedAlert, variant: 'flat' | 'card' = 'flat') => {
    const { title, subtitle, timestamp, status: rowStatus, tone } = listedAlertToAlertRow(alert, now);
    const isCard = variant === 'card';

    return (
      <AlertListItem
        key={alert.id}
        variant={variant}
        className={isCard ? CAROUSEL_CARD_CLASS : undefined}
        // Figma's card draws the type tile at 36px.
        icon={<AlertTypeIcon alert={alert} className={isCard ? 'size-9' : undefined} />}
        title={title}
        subtitle={subtitle}
        timestamp={timestamp}
        status={rowStatus}
        tone={tone}
        selected={alert.id === selectedId}
        showBlockedNotice={alert.id === blockedNoticeAlertId}
        blockedNoticeText={blockedNoticeText}
        onClick={onSelect ? () => onSelect(alert) : undefined}
        onDismiss={onDismiss ? () => onDismiss(alert.id) : undefined}
      />
    );
  };

  // The pill no longer spans the card, so it cannot hide the chrome it is
  // replacing: the card has to dissolve with it, on the same delay, or its
  // flanks pop in and out around the capsule.
  const pillHandoffClass =
    showsPillOverlay &&
    cx(
      FADE_CLASS,
      isPillVisible ? 'opacity-0' : 'opacity-100',
      isPillVisible && motion?.stage === 'active' && PILL_FADE_IN_DELAY_CLASS
    );

  const pillOverlay =
    showsPillOverlay &&
    renderPill(
      undefined,
      cx(
        // Centred on its own rather than stretched: the capsule hugs its
        // content, so there is no `inset-x-0` to hang it from.
        'absolute bottom-0 left-1/2 -translate-x-1/2',
        FADE_CLASS,
        isPillVisible ? 'opacity-100' : 'opacity-0',
        isPillVisible && motion.stage === 'active' && PILL_FADE_IN_DELAY_CLASS
      )
    );

  if (isCarouselTree) {
    return (
      <>
        <div
          role="region"
          className={cx(
            REGION_CLASS,
            'gap-3 pt-2',
            isMoving && 'h-full min-h-0 overflow-hidden',
            // Out of the full center the card shrinks from the pane's height,
            // so the carousel rides its bottom edge rather than its top.
            motion?.from === 'full' && 'justify-end',
            pillHandoffClass,
            className
          )}
          aria-label={ALERTS_CENTER_TITLE}
        >
          <AlertsCenterHeader
            title={ALERTS_CENTER_TITLE}
            icon={ChevronIcon}
            collapseLabel="Collapse alerts"
            onCollapse={onCollapse}
            collapseButtonRef={collapseButtonRef}
            layout="carousel"
            onViewAll={onViewAll}
          />
          <div {...(isMoving ? INERT_ATTRIBUTE : undefined)} className={CAROUSEL_TRACK_CLASS}>
            {needAttentionRows.map((alert) => renderRow(alert, 'card'))}
          </div>
        </div>

        {pillOverlay}
      </>
    );
  }

  const fullContentFadeClass = cx(FADE_CLASS, isFullContentVisible ? 'opacity-100' : 'opacity-0');

  return (
    <>
      <div
        role="region"
        className={cx(
          REGION_CLASS,
          fillsSheet ? 'bg-app_light' : CARD_CHROME_CLASS,
          'pt-3',
          showsViewAll && 'pb-4',
          (isFullTree || isMoving) && 'h-full min-h-0',
          isMoving && 'overflow-hidden',
          pillHandoffClass,
          className
        )}
        aria-label={ALERTS_CENTER_TITLE}
      >
        <AlertsCenterHeader
          // The full center carries its count inside the title, at full weight;
          // the truncated card sets "N of M" beside it, muted.
          title={isFullTree ? `${ALERTS_CENTER_TITLE} (${shownCount})` : ALERTS_CENTER_TITLE}
          count={isFullTree ? undefined : truncatedCount}
          icon={ChevronIcon}
          collapseLabel="Collapse alerts"
          onCollapse={onCollapse}
          collapseButtonRef={collapseButtonRef}
          fillsSheet={fillsSheet}
        />

        {/* The full center's body scrolls at rest; while moving it clips, so no
            scrollbar flashes mid-growth, and goes inert, so a row that is
            fading or sliding out of the clip cannot take a click or focus. Its
            spacing tweens between the two presentations' at the panel's
            duration. */}
        <div
          {...(isMoving ? INERT_ATTRIBUTE : undefined)}
          style={isMoving ? SIDE_PANEL_TRANSITION_DURATION_STYLE : undefined}
          className={cx(
            'flex min-h-0 flex-1 flex-col px-4',
            // In the sheet the first group sits 12px under the 56px band, as
            // the Figma draws it.
            isFullContentVisible ? cx('gap-6 pb-4', fillsSheet ? 'pt-3' : 'pt-4') : 'gap-2 py-2',
            isMoving
              ? 'overflow-y-hidden transition-[padding,gap] ease-in-out motion-reduce:transition-none'
              : isFullTree && 'overflow-y-auto'
          )}
        >
          {isFullTree && status === 'loading' && (
            <div className="flex flex-col items-center justify-center gap-3 py-16">
              <LoadingIndicator size="md" />
              <div className="text-tertiary text-sm">Loading your alerts…</div>
            </div>
          )}

          {isFullTree && status === 'error' && <AlertsCenterErrorState onRetry={onRetry} />}

          {isFullTree && status === 'empty' && <AlertsCenterEmptyState />}

          {showSections &&
            visibleSections.map(({ section, count, rows, hasMore, isLoadingMore, onLoadMore }) => {
              const alertSection = (
                <AlertSection
                  key={section}
                  section={section}
                  count={count}
                  labeled={showsFullContent}
                  labelRevealed={isFullContentVisible}
                >
                  {rows.map((alert) => renderRow(alert))}
                  {hasMore && onLoadMore && (
                    <div
                      aria-hidden={isFullContentVisible ? undefined : true}
                      className={cx('mt-4 flex justify-center', fullContentFadeClass)}
                    >
                      <Button
                        size="sm"
                        color="tertiary"
                        onClick={onLoadMore}
                        isLoading={isLoadingMore}
                        isDisabled={isLoadingMore}
                      >
                        Show more
                      </Button>
                    </div>
                  )}
                </AlertSection>
              );

              // The need-attention group is the one both cards share, so it
              // stays bare; the groups only the full center shows fade as one.
              return section === 'needAttention' ? (
                alertSection
              ) : (
                <div
                  key={section}
                  aria-hidden={isFullContentVisible ? undefined : true}
                  className={fullContentFadeClass}
                >
                  {alertSection}
                </div>
              );
            })}
        </div>

        {showsViewAll && (
          <button
            type="button"
            onClick={onViewAll}
            {...(isViewAllVisible ? undefined : INERT_ATTRIBUTE)}
            className={cx(
              'border-secondary_alt text-brand-secondary hover:text-brand-secondary_hover outline-focus-ring mx-4 flex h-10 shrink-0 cursor-pointer items-center justify-center rounded-xl border text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2',
              FADE_CLASS,
              isViewAllVisible ? 'opacity-100' : 'opacity-0'
            )}
          >
            View all
          </button>
        )}

        {/* Outside the scroller so a failed reconnect stays put while the list
            scrolls. Its own `mx-4` matches the body's inset. */}
        {isFullTree && actionSlot}
      </div>

      {pillOverlay}
    </>
  );
};

export default AlertsCenterBase;
